import { Box, Stack } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useTransactionsSum } from "Api/Queries/Transactions/useTransactionsSum";
import { MerchantPaymentComponentSkeleton } from "Components/Merchant/Payment/MerchantPaymentComponentSkeleton";
import { AccountBalance } from "Components/Shared/AccountBalance";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { PlusMathIcon } from "Components/Shared/Icons";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useIsUserVerified } from "Hooks/useIsUserVerified";
import { useUserMarketCurrency } from "Hooks/useUserMarketCurrency";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { hasPreApprovedAccount } from "Utils/AccountUtils";
import { appUrl } from "Utils/UrlUtils";
import { endOfDay, startOfDay } from "date-fns";
import type { ReactNode } from "react";

const PageResources = Resources.Merchant.Payment;
const NO_PRIMARY_RESOURCES = Resources.Components.NoPrimaryAccountPopup;

export const MerchantPaymentComponent = () => {
  const { t } = useResource();
  const marketCurrency = useUserMarketCurrency();
  const { isUserVerified, isLoading: isLoadingUserVerification } =
    useIsUserVerified();
  const { data: accounts, isLoading: isLoadingAccounts } = useBankingAccounts();
  const { data: transactionsSum, isLoading: isLoadingTransactionsSum } =
    useTransactionsSum({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
      allowedStatuses: [
        TransactionStatus.Success,
        // TransactionStatus.Processing,
      ],
      variant: "incoming",
    });

  const isLoading = isLoadingTransactionsSum || isLoadingAccounts;
  const isError = !accounts || !transactionsSum;

  if (isLoading) {
    return (
      <Layout>
        <MerchantPaymentComponentSkeleton />
      </Layout>
    );
  }

  if (isError) {
    return <Layout>{t(PageResources.LoadingError)}</Layout>;
  }

  // Primary is per currency: the CTA mints a payment in the market currency (the same rail the
  // balance tile below shows), so a foreign-currency primary must neither enable the button nor
  // lend it its bank logo — the initiation would only die on the backend currency guard.
  const primaryAccount = accounts.items.find(
    ({ isPrimary, currency }) => isPrimary && currency === marketCurrency,
  );
  const isPreApproved = hasPreApprovedAccount(accounts.items);
  const isDisabled =
    isLoadingUserVerification || (!isUserVerified && !isPreApproved);

  // One entry per currency comes back; this tile shows the merchant's market
  // currency (falls back to the first returned one).
  // The API enum and Models/CurrencyCodes are separate TS enums with identical
  // string values, so the comparison goes through string.
  const displayedSum =
    transactionsSum.totalSums.find(
      (sum) => (sum.currencyCode as string) === marketCurrency,
    ) ?? transactionsSum.totalSums[0];

  return (
    <Layout>
      <Stack gap={6}>
        <AccountBalance
          balance={displayedSum?.value ?? 0}
          currency={displayedSum?.currencyCode ?? marketCurrency}
          bank={primaryAccount?.bank}
        />
        {primaryAccount ? (
          <ButtonLink
            color="primary"
            variant="contained"
            startIcon={<PlusMathIcon width={20} height={20} />}
            text={t(PageResources.InitiatePaymentButton)}
            link={`${appUrl("initiate-form-payment")}?publicID=${encodeURIComponent(primaryAccount.publicID)}`}
            disabled={isDisabled}
          />
        ) : (
          <>
            <InfoBox
              variant="warning"
              content={t(NO_PRIMARY_RESOURCES.Message)}
            />
            <ButtonLink
              color="primary"
              variant="outlined"
              fullWidth
              link={appUrl("bank-address-list")}
              text={t(NO_PRIMARY_RESOURCES.Accounts)}
            />
          </>
        )}
      </Stack>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <PrimaryPaper>
      <PaperTitle>{t(PageResources.Title)}</PaperTitle>
      <Box>{children}</Box>
    </PrimaryPaper>
  );
};
